import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { DiagramType } from '../../tools/interfaces/ITool';
import type { CommandManager } from '../../commands/CommandManager';
import type { Node, Edge } from '../../models/scene';
import type { FieldDefinition } from '../../assistant/components/ViewRuleBuilder';
import type { IFrame } from '../../frames/IFrame';
import type { AgentMessage, AgentToolCall, AgentToolResult, AgentFormAutoExecute, AgentLensRecommendation, HighlightStore, ScenariosStore, ViewRulesStore } from './IAgentTool';
import { AgentExecutor } from './AgentExecutor';
import { stripLeakedToolResults } from './leakedToolResults';
import { AgentToolRegistry } from './AgentToolRegistry';
import type { OrgChartViewpoint } from '../../../stores/orgchartViewStore';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import type {
  SuggestionTreeRequest,
} from '../../../domains/orgchart/models/agentRequests';
import { AgentRequestError, streamAgentChatTurn, streamSuggestionTree } from './api/agentApiClient';
import { inferExecutionPolicy } from './ExecutionPolicy';
import { shouldEscalateSuggestionToExecution } from './suggestionBudget';
import {
  getAdaptiveSuggestionChildrenTimeoutMs,
  getSuggestionPrefetchJoinWaitMs,
  updateSuggestionLatencyEwma,
} from './suggestionTiming';

export interface ChatMessagePartText {
  type: 'text';
  text: string;
}

export interface ChatMessagePartToolResult {
  type: 'tool-result';
  toolName: string;
  params?: Record<string, unknown>;
  result: AgentToolResult;
}

export interface ChatMessagePartPreview {
  type: 'preview';
  toolName: string;
  result: AgentToolResult;
}

export interface ChatMessagePartFormInput {
  type: 'form-input';
  toolName: string;
  result: AgentToolResult;
}

export interface ChatMessagePartLensRequest {
  type: 'lens-request';
  toolName: string;
  result: AgentToolResult;
}

export type ChatMessagePart = ChatMessagePartText | ChatMessagePartToolResult | ChatMessagePartPreview | ChatMessagePartFormInput | ChatMessagePartLensRequest;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  parts: ChatMessagePart[];
  createdAt: number;
}

export interface AgentActionRecord {
  turnId: string;
  toolName: string;
  params: Record<string, unknown>;
  createdResourceIds: string[];
  undoMechanism: 'remove-rule' | 'command-undo' | 'scenario-discard';
  timestamp: number;
}

interface DirectPreviewRecord {
  toolName: string;
  result: AgentToolResult;
  createdAt: number;
}

export interface AgentAuditContext {
  diagramId?: string | null;
  folderId?: string | null;
}

export interface UseAgentOptions {
  registry: AgentToolRegistry;
  diagramData: { nodes: Node[]; edges: Edge[]; fields: FieldDefinition[]; frames?: IFrame[] };
  getLatestDiagramData?: () => { nodes: Node[]; edges: Edge[]; fields: FieldDefinition[]; frames?: IFrame[] };
  structuralDiagramData?: { nodes: Node[]; edges: Edge[]; fields: FieldDefinition[] };
  diagramType: DiagramType;
  selectedNodeIds: string[];
  canWrite: boolean;
  /** BCP-47 UI language (e.g. ar, en-US). Passed to the agent API so the model emits user-visible text in that language. */
  uiLocale?: string;
  assistantContext?: Record<string, unknown>;
  auditContext?: AgentAuditContext;
  commandManager: CommandManager;
  stores: {
    viewRules: ViewRulesStore;
    scenarios: ScenariosStore;
    highlight?: HighlightStore;
    orgchartView?: {
      asOfDate: string | null;
      collapsedNodeIds: string[];
      focusScope?: import('../../../domains/orgchart/models/FocusScope').FocusScope;
      setAsOfDate: (value: string | null) => void;
      setActiveViewpoint?: (value: OrgChartViewpoint) => void;
      setFocusScope?: (scope: import('../../../domains/orgchart/models/FocusScope').FocusScope) => void;
      clearFocusScope?: () => void;
      setCollapsedNodeIds?: (nodeIds: string[]) => void;
      toggleCollapsed?: (nodeId: string) => void;
      requestViewportFit?: () => void;
    };
  };
  llmEndpoint?: string;
  onToolResult?: (
    toolName: string,
    result: AgentToolResult,
    meta?: { previewMessageId?: string | null },
  ) => void;
}

interface SuggestionBranchNode {
  question: string;
  options: string[];
}

// Replay earlier tool results as real tool-call/tool-result pairs so the LLM
// keeps their data (node IDs, scenario IDs) without seeing them as text it wrote
// itself, which it would then imitate instead of calling tools.
export const buildConversationHistory = (messages: ChatMessage[]): AgentMessage[] =>
  messages.flatMap((msg): AgentMessage[] => {
    const textPart = msg.parts.find((p) => p.type === 'text');
    const text = textPart && textPart.type === 'text' ? textPart.text : '';
    if (msg.role === 'user') {
      return [{ role: 'user', content: text }];
    }
    const assistantText = stripLeakedToolResults(text);
    const toolParts = msg.parts.filter((p): p is ChatMessagePartToolResult => p.type === 'tool-result');
    if (toolParts.length === 0) {
      return [{ role: 'assistant', content: assistantText }];
    }
    const toolCalls = toolParts.map((tp, index) => ({ id: `${msg.id}-tool-${index}`, name: tp.toolName, params: tp.params ?? {} }));
    const history: AgentMessage[] = [
      { role: 'assistant', content: '', toolCalls },
      {
        role: 'tool',
        content: toolParts.map((tp, index) => ({
          toolCallId: toolCalls[index].id,
          toolName: tp.toolName,
          result: JSON.stringify({ success: tp.result.success, summary: tp.result.summary, ...(tp.result.data ? { data: tp.result.data } : {}) }),
        })),
      },
    ];
    if (assistantText) history.push({ role: 'assistant', content: assistantText });
    return history;
  });

const generateId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const SUGGESTIONS_PATTERN = /\[SUGGESTIONS:\s*(.+?)\]\s*$/;
export const PREVIEW_CONFIRM_TTL_MS = 5 * 60 * 1000;
export const isPreviewConfirmationExpired = (
  createdAt: number,
  now: number = Date.now(),
): boolean => now - createdAt > PREVIEW_CONFIRM_TTL_MS;
export const isAgentAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException || error instanceof Error) {
    return error.name === 'AbortError';
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  return (error as { name?: unknown }).name === 'AbortError';
};
const normalizeSuggestionToken = (value: string) =>
  value.trim().replace(/\s+/g, ' ').toLowerCase();
const QUESTION_PREFIXES = [
  'what ',
  'who ',
  'which ',
  'when ',
  'where ',
  'why ',
  'how ',
  'can ',
  'should ',
  'would ',
  'is ',
  'are ',
  'do ',
  'does ',
];
const suggestionRequestKey = (
  question: string,
  options: string[],
  pathPrefix: string[],
) =>
  `${normalizeSuggestionToken(question)}::${pathPrefix
    .map((segment) => normalizeSuggestionToken(segment))
    .join('\u001f')}::${options
    .map((option) => normalizeSuggestionToken(option))
  .join('\u001f')}`;

function extractSuggestions(text: string): { cleanText: string; suggestions: string[] } {
  const match = text.match(SUGGESTIONS_PATTERN);
  if (!match) return { cleanText: text, suggestions: [] };
  const suggestions = match[1].split('|').map((s) => s.trim()).filter(Boolean);
  const cleanText = text.slice(0, match.index).trimEnd();
  return { cleanText, suggestions };
}

function extractToolSuggestions(result: AgentToolResult): string[] {
  const data = result.data ?? {};
  const candidates = [data.suggestions, data.suggestedFollowUps];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const parsed = candidate
      .map((item) => String(item ?? '').trim())
      .filter((item) => item.length > 0);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

function buildToolNotes(results: Array<{ call: { name: string }; result: AgentToolResult }>): string[] {
  const lines: string[] = [];
  for (const item of results) {
    const preview = item.result.data?.conflictPreview;
    if (!Array.isArray(preview) || preview.length === 0) continue;
    const firstThree = preview.slice(0, 3) as Array<{ nodeName?: unknown; field?: unknown; oldValue?: unknown; newValue?: unknown }>;
    const note = firstThree
      .map((c) => `${String(c.nodeName ?? 'person')} ${String(c.field ?? 'field')}: ${String(c.oldValue ?? 'empty')} -> ${String(c.newValue ?? 'empty')}`)
      .join('; ');
    lines.push(`Review highlights: ${note}${preview.length > 3 ? ` (+${preview.length - 3} more)` : ''}`);
  }
  return lines;
}

function buildForcedAssistantText(
  results: Array<{ call: { name: string }; result: AgentToolResult }>
): string | null {
  const genogramChange = results.find((r) => r.call.name === 'genogram_proposed_changes');
  if (genogramChange?.result.success) {
    if (!genogramChange.result.preview) {
      return 'Done. Update applied. Use Undo if needed.';
    }

    return 'Proposed change ready. Review and confirm to apply.';
  }

  const structuralNetworkChange = results.find((r) =>
    r.result.success
    && ['create_resource', 'connect_resources', 'apply_pattern', 'compose_service', 'rdm_apply'].includes(r.call.name)
  );
  if (!structuralNetworkChange) return null;

  const summary = stripFinalPeriod(structuralNetworkChange.result.summary);
  if (structuralNetworkChange.result.preview) {
    return `Proposed change ready: ${summary}. Review it, then apply if it looks right.`;
  }

  return `Done. ${summary}. Use Undo if needed.`;
}

function stripFinalPeriod(text: string): string {
  return text.trim().replace(/[.]+$/u, '');
}

function sanitizeSuggestionList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const cleaned = input
    .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
    .map((text) => text
      .replace(/\r?\n+/g, ' ')
      .replace(/[*_`~#>[\]()]/g, '')
      .replace(/^[-*•]\s+/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[.,;:]+$/g, ''))
    .filter((text) => text.length > 0)
    .filter((text) => !text.includes('?'))
    .filter((text) => !QUESTION_PREFIXES.some((prefix) => text.toLowerCase().startsWith(prefix)))
    .map((text) => text.slice(0, 40).trim())
    .filter((text) => text.length > 0);
  return Array.from(new Set(cleaned)).slice(0, 6);
}

function previewCreatedResourceIds(result: AgentToolResult): string[] {
  const additions = result.preview?.delta.additions ?? [];
  return additions
    .map((addition) => addition.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function useAgent(options: UseAgentOptions) {
  const {
    registry,
    diagramData,
    getLatestDiagramData,
    structuralDiagramData,
    diagramType,
    selectedNodeIds,
    canWrite,
    uiLocale,
    commandManager,
    stores,
    llmEndpoint = '/api/ai/agent/chat/stream',
    assistantContext,
    auditContext,
    onToolResult,
  } = options;
  const { t } = useTranslation('diagram');
  const getCurrentDiagramData = useCallback(() => {
    if (typeof getLatestDiagramData === 'function') {
      const latest = getLatestDiagramData();
      if (latest?.nodes && latest?.edges && latest?.fields) {
        return latest;
      }
    }
    return diagramData;
  }, [diagramData, getLatestDiagramData]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTrace, setLoadingTrace] = useState<string[]>([]);
  const [liveThinkingText, setLiveThinkingText] = useState('');
  const [pendingPreviewTurnId, setPendingPreviewTurnId] = useState<string | null>(null);
  const [pendingFormTurnId, setPendingFormTurnId] = useState<string | null>(null);
  const [pendingLensTurnId, setPendingLensTurnId] = useState<string | null>(null);
  const [actionHistory, setActionHistory] = useState<AgentActionRecord[]>([]);
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const previewTimeoutRef = useRef<number | null>(null);
  const isLoadingRef = useRef(false);
  const streamObserverRef = useRef<(event: { type: 'trace'; step: string } | { type: 'model-text'; text: string }) => void>(() => {});
  const onToolResultRef = useRef(onToolResult);
  onToolResultRef.current = onToolResult;
  const messagesRef = useRef<ChatMessage[]>([]);
  const suggestionTreeRef = useRef<Map<string, SuggestionBranchNode>>(new Map());
  const suggestionPathRef = useRef<string[]>([]);
  const suggestionRootQuestionRef = useRef<string | null>(null);
  const currentSuggestionQuestionRef = useRef<string | null>(null);
  const currentSuggestionOptionsRef = useRef<string[]>([]);
  const currentSuggestionAskBudgetRef = useRef<number>(2);
  const suggestionPrefetchSeqRef = useRef(0);
  const suggestionPrefetchInFlightRef = useRef<Map<string, Promise<void>>>(new Map());
  const directPreviewResultsRef = useRef<Map<string, DirectPreviewRecord>>(new Map());
  const resolvingPreviewMessageIdsRef = useRef<Set<string>>(new Set());
  const resolvedPreviewMessageIdsRef = useRef<Set<string>>(new Set());
  const lastExecutionPolicyRef = useRef<ReturnType<typeof inferExecutionPolicy> | null>(null);
  const authenticatedFetch = useAuthenticatedFetch();
  const suggestionLatencyEwmaRef = useRef<number>(2500);
  const timingSeqRef = useRef(0);
  const debugTimingsEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const localFlag = window.localStorage.getItem('ai.debug.timings') === '1';
    const urlFlag = new URLSearchParams(window.location.search).has('aiTimingDebug');
    return localFlag || urlFlag || import.meta.env.DEV;
  }, []);
  const logTiming = useCallback((event: string, payload: Record<string, unknown> = {}) => {
    if (!debugTimingsEnabled) return;
    console.log('[ai-timing]', {
      event,
      t: Math.round(performance.now()),
      ...payload,
    });
  }, [debugTimingsEnabled]);
  const auditAgentMutation = useCallback(async (
    toolName: string,
    result: AgentToolResult,
    agentRunId: string,
    source: 'chat' | 'direct-tool' | 'preview-confirm',
  ) => {
    if (!auditContext?.diagramId || !result.success) return;
    const tool = registry.getTool(toolName);
    if (!tool || tool.tier === 'safe') return;
    const createdResourceIds = result.createdResourceIds?.length
      ? result.createdResourceIds
      : previewCreatedResourceIds(result);
    try {
      await authenticatedFetch('/api/ai/agent/mutation-audit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          diagramId: auditContext.diagramId,
          folderId: auditContext.folderId ?? null,
          diagramType,
          toolName,
          toolTier: tool.tier,
          toolCategory: tool.category,
          agentRunId,
          summary: result.summary,
          createdResourceIds,
          metadata: {
            source,
            resultDataKeys: Object.keys(result.data ?? {}).slice(0, 40),
          },
        }),
      });
    } catch {
      // Audit writes are best-effort from the client; the server is
      // authoritative and fail-closed for rows it accepts.
    }
  }, [auditContext?.diagramId, auditContext?.folderId, authenticatedFetch, diagramType, registry]);
  const recordSuggestionLatency = useCallback((elapsedMs: number) => {
    suggestionLatencyEwmaRef.current = updateSuggestionLatencyEwma(
      suggestionLatencyEwmaRef.current,
      elapsedMs,
    );
  }, []);
  const getAdaptiveChildrenTimeoutMs = useCallback(() => {
    return getAdaptiveSuggestionChildrenTimeoutMs(suggestionLatencyEwmaRef.current);
  }, []);

  const suggestionPathKey = (path: string[]) =>
    path.map((segment) => normalizeSuggestionToken(segment)).join('\u001f');

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const resetSuggestionTree = useCallback(() => {
    suggestionTreeRef.current.clear();
    suggestionPrefetchInFlightRef.current.clear();
    suggestionPathRef.current = [];
    suggestionRootQuestionRef.current = null;
    currentSuggestionQuestionRef.current = null;
    currentSuggestionOptionsRef.current = [];
    currentSuggestionAskBudgetRef.current = 2;
    suggestionPrefetchSeqRef.current += 1;
  }, []);


  const suggestionTreeStreamMutation = useMutation({
    mutationFn: async (payload: {
      request: SuggestionTreeRequest;
      handlers: {
        onBranch?: (branch: { option: string; question: string; options: string[] }) => void;
        onNext?: (parentOption: string, next: { option: string; question: string; options: string[] }) => void;
      };
    }) => streamSuggestionTree(payload.request, payload.handlers, authenticatedFetch),
  });
  const streamSuggestionTreeMutation = suggestionTreeStreamMutation.mutateAsync;

  const agentChatStreamMutation = useMutation({
    mutationFn: async (payload: {
      endpoint: string;
      body: {
        messages: AgentMessage[];
        diagramType: string;
        diagramSummary: {
          nodeCount: number;
          edgeCount: number;
          fields: string[];
          departments?: string[];
          availableTitles?: string[];
          contextScope?: {
            requiredFields: string[];
            clarifyingQuestionScope: string;
            contextShape: string;
          };
          contextPayload?: Record<string, unknown>;
        };
        availableTools: string[];
        toolDefinitions: Array<{
          name: string;
          description: string;
          parameters: Record<string, unknown>;
          tier: string;
          category: string;
        }>;
        uiLocale?: string;
      };
      signal?: AbortSignal;
      onTrace?: (step: string) => void;
      onTextDelta?: (delta: string) => void;
    }) => streamAgentChatTurn({ ...payload, requestFn: authenticatedFetch }),
  });
  const streamAgentChatTurnMutation = agentChatStreamMutation.mutateAsync;

  const buildSuggestionTreeRequest = useCallback((
    rootQuestion: string,
    optionsForTree: string[],
    pathDepth: number,
    recentMessages: string[],
  ): SuggestionTreeRequest => {
    const currentDiagramData = getCurrentDiagramData();
    return ({
    diagramType,
    diagramSummary: {
      nodeCount: currentDiagramData.nodes.length,
      edgeCount: currentDiagramData.edges.length,
      fields: currentDiagramData.fields.map((f) => f.id),
      departments: Array.from(
        new Set(
          currentDiagramData.nodes
            .map((n) => (n.attrs?.department ?? n.props?.department))
            .filter((d): d is string => typeof d === 'string' && d.trim().length > 0),
        ),
      ).slice(0, 20),
      availableTitles: Array.from(
        new Set(
          currentDiagramData.nodes
            .map((n) => (n.attrs?.title ?? n.attrs?.role ?? n.props?.title))
            .filter((t): t is string => typeof t === 'string' && t.trim().length > 0),
        ),
      ).slice(0, 40),
    },
    rootQuestion,
    options: optionsForTree.slice(0, 6),
    executionPolicy: inferExecutionPolicy(rootQuestion),
    depth: 2,
    pathDepth,
    recentMessages: recentMessages.slice(-8),
    uiLocale,
  });
  }, [diagramType, getCurrentDiagramData, uiLocale]);

  const prefetchSuggestionTree = useCallback(
    async (
      rootQuestion: string,
      optionsForTree: string[],
      seedMessages: ChatMessage[],
      pathPrefix: string[] = [],
    ) => {
      if (!rootQuestion || optionsForTree.length < 2) return;
      const timingId = ++timingSeqRef.current;
      const startedAt = performance.now();
      const seq = suggestionPrefetchSeqRef.current + 1;
      suggestionPrefetchSeqRef.current = seq;
      suggestionRootQuestionRef.current = rootQuestion;
      const requestKey = suggestionRequestKey(rootQuestion, optionsForTree, pathPrefix);
      const existing = suggestionPrefetchInFlightRef.current.get(requestKey);
      if (existing) {
        logTiming('prefetch.join-existing.start', {
          timingId,
          pathDepth: pathPrefix.length,
          optionCount: optionsForTree.length,
        });
        await existing;
        logTiming('prefetch.join-existing.done', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        return;
      }
      logTiming('prefetch.start', {
        timingId,
        pathDepth: pathPrefix.length,
        optionCount: optionsForTree.length,
      });

      const inFlight = (async () => {
        try {
          const request = buildSuggestionTreeRequest(
            rootQuestion,
            optionsForTree,
            pathPrefix.length,
            seedMessages
              .slice(-8)
              .map((m) => m.parts.find((p) => p.type === 'text'))
              .filter((p): p is { type: 'text'; text: string } => p?.type === 'text')
              .map((p) => p.text),
          );

          const mergeEntry = (key: string, value: SuggestionBranchNode) => {
            if (suggestionPrefetchSeqRef.current !== seq) return;
            const merged = new Map(suggestionTreeRef.current);
            merged.set(key, value);
            suggestionTreeRef.current = merged;
          };

          await streamSuggestionTreeMutation({
            request,
            handlers: {
              onBranch: (branch) => {
                const levelOneKey = suggestionPathKey([...pathPrefix, branch.option]);
                mergeEntry(levelOneKey, {
                  question: branch.question,
                  options: (branch.options ?? []).slice(0, 4),
                });
              },
              onNext: (parentOption, next) => {
                const levelTwoKey = suggestionPathKey([
                  ...pathPrefix,
                  parentOption,
                  next.option,
                ]);
                mergeEntry(levelTwoKey, {
                  question: next.question,
                  options: (next.options ?? []).slice(0, 4),
                });
              },
            },
          });
          logTiming('prefetch.stream.done', {
            timingId,
            elapsedMs: Math.round(performance.now() - startedAt),
          });
          recordSuggestionLatency(performance.now() - startedAt);
        } catch {
          logTiming('prefetch.stream.error', {
            timingId,
            elapsedMs: Math.round(performance.now() - startedAt),
          });
          // Silent fallback to normal sendMessage flow
        }
      })();
      suggestionPrefetchInFlightRef.current.set(requestKey, inFlight);
      try {
        await inFlight;
      } finally {
        suggestionPrefetchInFlightRef.current.delete(requestKey);
        logTiming('prefetch.finalize', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
      }
    },
    [buildSuggestionTreeRequest, logTiming, recordSuggestionLatency, streamSuggestionTreeMutation],
  );

  const fetchSuggestionChildren = useCallback(
    async (
      question: string,
      optionsForTree: string[],
      selectionPath: string[] = [],
      recentMessages: string[] = [],
      timeoutMs?: number,
    ) => {
      if (!question || optionsForTree.length < 2) return null;
      const timingId = ++timingSeqRef.current;
      const startedAt = performance.now();
      const effectiveTimeoutMs = timeoutMs ?? getAdaptiveChildrenTimeoutMs();
      logTiming('children.fetch.start', {
        timingId,
        pathDepth: selectionPath.length,
        optionCount: optionsForTree.length,
        timeoutMs: effectiveTimeoutMs,
      });
      try {
        const contextualQuestion = selectionPath.length > 0
          ? `${question}\nContext path: ${selectionPath.join(' > ')}`
          : question;
        const request = buildSuggestionTreeRequest(
          contextualQuestion,
          optionsForTree,
          selectionPath.length,
          recentMessages,
        );
        request.depth = 1;
        const response = await Promise.race([
          streamSuggestionTreeMutation({ request, handlers: {} }),
          new Promise<null>((resolve) => window.setTimeout(() => resolve(null), effectiveTimeoutMs)),
        ]);
        if (response) {
          recordSuggestionLatency(performance.now() - startedAt);
        }
        logTiming(response ? 'children.fetch.done' : 'children.fetch.timeout', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        return response;
      } catch {
        logTiming('children.fetch.error', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        return null;
      }
    },
    [buildSuggestionTreeRequest, logTiming, getAdaptiveChildrenTimeoutMs, recordSuggestionLatency, streamSuggestionTreeMutation],
  );

  const executor = useMemo(() => {
    const streamEndpoint = llmEndpoint.endsWith('/stream') ? llmEndpoint : `${llmEndpoint.replace(/\/$/, '')}/stream`;
    return new AgentExecutor({
      registry,
      llmClient: async ({ messages, diagramType, diagramSummary, availableTools, toolDefinitions, abortSignal }) => {
        return streamAgentChatTurnMutation({
          endpoint: streamEndpoint,
          body: {
            messages,
            diagramType,
            diagramSummary,
            availableTools,
            toolDefinitions,
            ...(uiLocale ? { uiLocale } : {}),
          },
          signal: abortSignal,
          onTrace: (step) => {
            streamObserverRef.current({ type: 'trace', step });
          },
        });
      },
    });
  }, [registry, llmEndpoint, streamAgentChatTurnMutation, uiLocale]);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsLoading(false);
    setLoadingTrace([]);
    setLiveThinkingText('');
  }, []);

  useEffect(() => {
    if (!pendingPreviewTurnId) return;
    if (previewTimeoutRef.current) {
      window.clearTimeout(previewTimeoutRef.current);
    }
    previewTimeoutRef.current = window.setTimeout(() => {
      resolvedPreviewMessageIdsRef.current.add(pendingPreviewTurnId);
      setPendingPreviewTurnId(null);
      previewTimeoutRef.current = null;
    }, PREVIEW_CONFIRM_TTL_MS);
    return () => {
      if (previewTimeoutRef.current) {
        window.clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
    };
  }, [pendingPreviewTurnId]);

  const sendMessageInternal = useCallback(async (content: string, userFacingContent?: string, silent?: boolean) => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    const timingId = ++timingSeqRef.current;
    const startedAt = performance.now();
    logTiming('chat.turn.start', {
      timingId,
      hasUserFacingOverride: Boolean(userFacingContent),
      inputLength: content.length,
    });

    const turnId = generateId('turn');
    const visibleUserText = userFacingContent ?? content;
    const userMessage: ChatMessage = {
      id: generateId('msg'),
      role: 'user',
      parts: [{ type: 'text', text: visibleUserText }],
      createdAt: Date.now(),
    };
    if (!silent) {
      setMessages((prev) => [...prev, userMessage]);
    }
    setDynamicSuggestions(null);
    setPendingPreviewTurnId(null);
    setPendingFormTurnId(null);
    setPendingLensTurnId(null);
    resetSuggestionTree();

    const abortController = new AbortController();
    abortRef.current = abortController;
    setIsLoading(true);
    setLoadingTrace(['Understanding your request']);
    setLiveThinkingText('');
    const pushTrace = (step: string) => setLoadingTrace((prev) => (prev.includes(step) ? prev : [...prev, step]));
    streamObserverRef.current = (event) => {
      if (event.type === 'trace') {
        pushTrace(event.step);
        return;
      }
      setLiveThinkingText(event.text);
    };

    try {
      const conversationHistory = buildConversationHistory(messages);

      const result = await executor.execute(
        content,
        {
          diagramData: getCurrentDiagramData(),
          ...(getLatestDiagramData ? { getLatestDiagramData: getCurrentDiagramData } : {}),
          structuralDiagramData,
          diagramType,
          ...(uiLocale ? { uiLocale } : {}),
          selectedNodeIds,
          canWrite,
          assistantContext,
          commandManager,
          stores,
        },
        abortController.signal,
        conversationHistory,
      );
      logTiming('chat.turn.executor.done', {
        timingId,
        elapsedMs: Math.round(performance.now() - startedAt),
        toolResultCount: result.toolResults.length,
      });
      pushTrace('Applying tool plan');

      // Check for structured suggestions from suggest tool (priority)
      let toolSuggestions: string[] | null = null;
      let suggestQuestion: string | null = null;
      for (const toolResult of result.toolResults) {
        if (toolResult.call.name === 'suggest' && toolResult.result.data?.suggestions) {
          const sanitized = sanitizeSuggestionList(toolResult.result.data.suggestions);
          toolSuggestions = sanitized.length > 0 ? sanitized : null;
          const rawQuestion = toolResult.result.data.question;
          const normalizedQuestion =
            typeof rawQuestion === 'string' ? rawQuestion.trim() : String(rawQuestion ?? '').trim();
          suggestQuestion = normalizedQuestion.length > 0 ? normalizedQuestion : null;
        }
      }

      // Always clean text of [SUGGESTIONS: ...] markers (fallback pattern)
      const { cleanText, suggestions: parsedSuggestionsRaw } = extractSuggestions(result.text);
      const parsedSuggestions = sanitizeSuggestionList(parsedSuggestionsRaw);

      // Tool suggestions take priority over regex
      const resolvedSuggestions = toolSuggestions ?? (parsedSuggestions.length > 0 ? parsedSuggestions : null);
      setDynamicSuggestions(resolvedSuggestions);
      if (resolvedSuggestions && resolvedSuggestions.length > 0) {
        currentSuggestionOptionsRef.current = resolvedSuggestions;
        const inferredPolicy = inferExecutionPolicy(
          currentSuggestionQuestionRef.current ?? suggestQuestion ?? content,
        );
        if (lastExecutionPolicyRef.current?.mode === 'scenario_planning') {
          inferredPolicy.askBudget = Math.max(inferredPolicy.askBudget, 3);
        }
        currentSuggestionAskBudgetRef.current = inferredPolicy.askBudget;
        lastExecutionPolicyRef.current = inferredPolicy;
      }

      // Suggestions supplement the answer. A follow-up question can be part of
      // a complete analysis, so its presence must never erase that analysis.
      let messageText = cleanText || suggestQuestion || '';
      if (suggestQuestion) {
        currentSuggestionQuestionRef.current = suggestQuestion;
      } else if (messageText) {
        currentSuggestionQuestionRef.current = messageText;
      }

      const normalizedResults = result.toolResults.map((r) => ({ call: { name: r.call.name }, result: r.result }));
      const forcedText = buildForcedAssistantText(normalizedResults);
      const toolNotes = buildToolNotes(normalizedResults);
      if (forcedText) {
        messageText = forcedText;
      }
      if (toolNotes.length > 0) {
        messageText = `${messageText}${messageText ? '\n\n' : ''}${toolNotes.join('\n')}`;
      }

      const parts: ChatMessagePart[] = [{ type: 'text', text: messageText }];
      let hasPreview = false;
      let hasFormRequest = false;
      let hasLensRequest = false;
      for (const toolResult of result.toolResults) {
        parts.push({ type: 'tool-result', toolName: toolResult.call.name, params: toolResult.call.params, result: toolResult.result });
        if (toolResult.result.preview) {
          parts.push({ type: 'preview', toolName: toolResult.call.name, result: toolResult.result });
          hasPreview = true;
        }
        if (toolResult.result.formRequest) {
          parts.push({ type: 'form-input', toolName: toolResult.call.name, result: toolResult.result });
          hasFormRequest = true;
        }
        if (toolResult.result.lensRequest) {
          parts.push({ type: 'lens-request', toolName: toolResult.call.name, result: toolResult.result });
          hasLensRequest = true;
        }

        if (toolResult.result.success && toolResult.result.createdResourceIds?.length) {
          setActionHistory((prev) => [
            ...prev,
            {
              turnId,
              toolName: toolResult.call.name,
              params: toolResult.call.params,
              createdResourceIds: toolResult.result.createdResourceIds ?? [],
              undoMechanism: toolResult.call.name === 'scenario' ? 'scenario-discard' : 'command-undo',
              timestamp: Date.now(),
            },
          ]);
        }
        void auditAgentMutation(
          toolResult.call.name,
          toolResult.result,
          `${turnId}:${toolResult.call.name}`,
          'chat',
        );
      }

      const assistantMessage: ChatMessage = {
        id: generateId('msg'),
        role: 'assistant',
        parts,
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      for (const toolResult of result.toolResults) {
        if (toolResult.result.uiSurface === 'insight_panel' || toolResult.result.uiSurface === 'both') {
          onToolResultRef.current?.(toolResult.call.name, toolResult.result, {
            previewMessageId: toolResult.result.preview ? assistantMessage.id : null,
          });
        }
      }
      if (suggestQuestion && toolSuggestions && toolSuggestions.length >= 2) {
        const seedMessages = [...messagesRef.current, userMessage, assistantMessage];
        void prefetchSuggestionTree(suggestQuestion, toolSuggestions, seedMessages);
      }
      if (hasPreview) {
        setPendingPreviewTurnId(assistantMessage.id);
      }
      if (hasFormRequest) {
        setPendingFormTurnId(assistantMessage.id);
      }
      if (hasLensRequest) {
        setPendingLensTurnId(assistantMessage.id);
      }
    } catch (error) {
      if (abortController.signal.aborted || isAgentAbortError(error)) {
        logTiming('chat.turn.cancelled', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        return;
      }
      logTiming('chat.turn.error', {
        timingId,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      if (error instanceof AgentRequestError) {
        console.error('[Agent] Request failed', JSON.stringify({
          phase: error.phase, status: error.status, code: error.code, requestId: error.requestId,
        }));
      }
      const assistantError: ChatMessage = {
        id: generateId('msg'),
        role: 'assistant',
        parts: [
          {
            type: 'text',
            text: error instanceof AgentRequestError && error.code === 'FEATURE_DISABLED'
              ? t('assistant.assistantErrorDisabled', {
                defaultValue: 'The AI assistant is not enabled for your organization. Contact your administrator.',
              }) : t('assistant.assistantErrorTemporary', {
              defaultValue: 'Assistant is temporarily unavailable. Please retry in a few seconds.',
            }),
          },
        ],
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, assistantError]);
    } finally {
      logTiming('chat.turn.finalize', {
        timingId,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      setIsLoading(false);
      setLoadingTrace([]);
      setLiveThinkingText('');
      streamObserverRef.current = () => {};
      isLoadingRef.current = false;
      abortRef.current = null;
    }
  }, [
    executor,
    getCurrentDiagramData,
    getLatestDiagramData,
    structuralDiagramData,
    diagramType,
    selectedNodeIds,
    canWrite,
    uiLocale,
    assistantContext,
    auditAgentMutation,
    commandManager,
    stores,
    messages,
    resetSuggestionTree,
    prefetchSuggestionTree,
    logTiming,
    t,
  ]);

  const sendMessage = useCallback(async (content: string) => {
    const policy = inferExecutionPolicy(content);
    lastExecutionPolicyRef.current = policy;
    await sendMessageInternal(content);
  }, [sendMessageInternal]);

  const invokeTool = useCallback(async (
    toolName: string,
    params: Record<string, unknown>,
    options?: { userMessage?: string; assistantText?: string }
  ) => {
    if (isLoadingRef.current) return null;
    isLoadingRef.current = true;

    const turnId = generateId('turn');
    if (options?.userMessage) {
      const userMessage: ChatMessage = {
        id: generateId('msg'),
        role: 'user',
        parts: [{ type: 'text', text: options.userMessage }],
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage]);
    }

    setDynamicSuggestions(null);
    setPendingPreviewTurnId(null);
    setIsLoading(true);
    setLoadingTrace([`Running ${toolName}`]);
    setLiveThinkingText('');

    try {
      const result = await registry.executeToolCall(toolName, params, {
        diagramData: getCurrentDiagramData(),
        structuralDiagramData,
        diagramType,
        selectedNodeIds,
        canWrite,
        uiLocale,
        assistantContext,
        commandManager,
        stores,
      });

      const noteText = buildToolNotes([{ call: { name: toolName }, result }]);
      const baseText = result.success ? (options?.assistantText ?? result.summary) : result.summary;
      const text = `${baseText}${noteText.length ? `\n\n${noteText.join('\n')}` : ''}`;
      const parts: ChatMessagePart[] = [
        { type: 'text', text },
        { type: 'tool-result', toolName, params, result },
      ];

      if (result.preview) {
        parts.push({ type: 'preview', toolName, result });
      }

      const toolSuggestions = extractToolSuggestions(result);
      setDynamicSuggestions(toolSuggestions.length > 0 ? toolSuggestions : null);

      if (result.success && result.createdResourceIds?.length) {
        setActionHistory((prev) => [
          ...prev,
          {
            turnId,
            toolName,
            params,
            createdResourceIds: result.createdResourceIds ?? [],
            undoMechanism: toolName === 'scenario' ? 'scenario-discard' : 'command-undo',
            timestamp: Date.now(),
          },
        ]);
      }
      void auditAgentMutation(toolName, result, `${turnId}:${toolName}`, 'direct-tool');

      const assistantMessage: ChatMessage = {
        id: generateId('msg'),
        role: 'assistant',
        parts,
        createdAt: Date.now(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      if (result.uiSurface === 'insight_panel' || result.uiSurface === 'both') {
        onToolResultRef.current?.(toolName, result, {
          previewMessageId: result.preview ? assistantMessage.id : null,
        });
      }
      if (result.preview) {
        setPendingPreviewTurnId(assistantMessage.id);
      }

      return result;
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown error';
      const assistantError: ChatMessage = {
        id: generateId('msg'),
        role: 'assistant',
        parts: [
          {
            type: 'text',
            text: t('assistant.assistantErrorTemporaryWithDetail', {
              detail,
              defaultValue: `Assistant is temporarily unavailable (${detail}). Please retry in a few seconds.`,
            }),
          },
        ],
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, assistantError]);
      return null;
    } finally {
      setIsLoading(false);
      setLoadingTrace([]);
      setLiveThinkingText('');
      isLoadingRef.current = false;
    }
  }, [
    registry,
    getCurrentDiagramData,
    structuralDiagramData,
    diagramType,
    selectedNodeIds,
    canWrite,
    uiLocale,
    assistantContext,
    commandManager,
    stores,
    t,
    auditAgentMutation,
  ]);

  const invokeToolSilently = useCallback(async (
    toolName: string,
    params: Record<string, unknown>,
  ) => {
    if (isLoadingRef.current) return null;
    isLoadingRef.current = true;

    const turnId = generateId('turn');
    setDynamicSuggestions(null);
    setPendingPreviewTurnId(null);
    setIsLoading(true);
    setLoadingTrace([`Running ${toolName}`]);
    setLiveThinkingText('');

    try {
      const result = await registry.executeToolCall(toolName, params, {
        diagramData: getCurrentDiagramData(),
        structuralDiagramData,
        diagramType,
        selectedNodeIds,
        canWrite,
        uiLocale,
        assistantContext,
        commandManager,
        stores,
      });

      const toolSuggestions = extractToolSuggestions(result);
      setDynamicSuggestions(toolSuggestions.length > 0 ? toolSuggestions : null);

      if (result.success && result.createdResourceIds?.length) {
        setActionHistory((prev) => [
          ...prev,
          {
            turnId,
            toolName,
            params,
            createdResourceIds: result.createdResourceIds ?? [],
            undoMechanism: toolName === 'scenario' ? 'scenario-discard' : 'command-undo',
            timestamp: Date.now(),
          },
        ]);
      }

      void auditAgentMutation(toolName, result, `${turnId}:${toolName}`, 'direct-tool');

      let previewMessageId: string | null = null;
      if (result.preview) {
        previewMessageId = generateId('direct-preview');
        directPreviewResultsRef.current.set(previewMessageId, {
          toolName,
          result,
          createdAt: Date.now(),
        });
        setPendingPreviewTurnId(previewMessageId);
      }

      if (result.uiSurface === 'insight_panel' || result.uiSurface === 'both') {
        onToolResultRef.current?.(toolName, result, { previewMessageId });
      }

      return result;
    } finally {
      setIsLoading(false);
      setLoadingTrace([]);
      setLiveThinkingText('');
      isLoadingRef.current = false;
    }
  }, [
    registry,
    getCurrentDiagramData,
    structuralDiagramData,
    diagramType,
    selectedNodeIds,
    canWrite,
    uiLocale,
    assistantContext,
    commandManager,
    stores,
    auditAgentMutation,
  ]);

  const resolvePendingPreview = useCallback(async (
    action: 'confirm' | 'reject',
    messageId?: string | null,
  ) => {
    const targetMessageId = messageId ?? pendingPreviewTurnId;
    if (!targetMessageId) return false;
    if (resolvingPreviewMessageIdsRef.current.has(targetMessageId)) return false;
    if (resolvedPreviewMessageIdsRef.current.has(targetMessageId)) return false;

    const directPreview = directPreviewResultsRef.current.get(targetMessageId);
    const previewMessage = messagesRef.current.find((m) => m.id === targetMessageId)
      ?? messages.find((m) => m.id === targetMessageId);
    const previewPart =
      directPreview
        ?? (previewMessage?.parts.find((p) => p.type === 'preview') as ChatMessagePartPreview | undefined);
    const previewCreatedAt = directPreview?.createdAt ?? previewMessage?.createdAt;
    if (!previewCreatedAt || !previewPart?.result.preview) {
      if (pendingPreviewTurnId === targetMessageId) {
        setPendingPreviewTurnId(null);
      }
      return false;
    }

    if (isPreviewConfirmationExpired(previewCreatedAt)) {
      resolvedPreviewMessageIdsRef.current.add(targetMessageId);
      directPreviewResultsRef.current.delete(targetMessageId);
      if (pendingPreviewTurnId === targetMessageId) {
        setPendingPreviewTurnId(null);
      }
      return false;
    }

    resolvingPreviewMessageIdsRef.current.add(targetMessageId);
    resolvedPreviewMessageIdsRef.current.add(targetMessageId);
    if (pendingPreviewTurnId === targetMessageId) {
      setPendingPreviewTurnId(null);
    }

    try {
      const context = {
        diagramData: getCurrentDiagramData(),
        structuralDiagramData,
        diagramType,
        selectedNodeIds,
        canWrite,
        uiLocale,
        assistantContext,
        commandManager,
        stores,
      };
      const resolution = await registry.resolvePreview(previewPart.result.preview, action, context);
      if (resolution?.success === false) {
        if (resolution.summary) {
          setMessages((previous) => [...previous, {
            id: generateId('preview-failed'),
            role: 'assistant',
            parts: [{ type: 'text', text: resolution.summary! }],
            createdAt: Date.now(),
          }]);
        }
        return false;
      }
      if (action === 'confirm') {
        await auditAgentMutation(
          previewPart.toolName,
          { ...previewPart.result, ...(resolution ?? {}) },
          `${targetMessageId}:${previewPart.toolName}:confirm`,
          'preview-confirm',
        );
      }
      directPreviewResultsRef.current.delete(targetMessageId);
      return true;
    } finally {
      directPreviewResultsRef.current.delete(targetMessageId);
      resolvingPreviewMessageIdsRef.current.delete(targetMessageId);
      if (pendingPreviewTurnId === targetMessageId) {
        setPendingPreviewTurnId(null);
      }
    }
  }, [
    pendingPreviewTurnId,
    messages,
    registry,
    getCurrentDiagramData,
    structuralDiagramData,
    diagramType,
    selectedNodeIds,
    canWrite,
    uiLocale,
    assistantContext,
    commandManager,
    stores,
    auditAgentMutation,
  ]);

  const confirmPendingPreview = useCallback(async (messageId?: string | null) => {
    return resolvePendingPreview('confirm', messageId);
  }, [resolvePendingPreview]);

  const rejectPendingPreview = useCallback(async (messageId?: string | null) => {
    return resolvePendingPreview('reject', messageId);
  }, [resolvePendingPreview]);

  const selectSuggestion = useCallback(async (suggestion: string) => {
    if (isLoadingRef.current) return;
    const timingId = ++timingSeqRef.current;
    const startedAt = performance.now();
    logTiming('chip.click.start', {
      timingId,
      suggestion,
      currentPathDepth: suggestionPathRef.current.length,
    });
    const currentPath = suggestionPathRef.current;
    const nextPath = [...currentPath, suggestion];
    const askBudget = Math.max(0, currentSuggestionAskBudgetRef.current);
    const shouldEscalateToExecution = shouldEscalateSuggestionToExecution(nextPath.length, askBudget);
    const rootQuestion = suggestionRootQuestionRef.current;
    const fullPath = [...currentPath, suggestion];
    const composedPrompt =
      rootQuestion && fullPath.length > 0
        ? `${rootQuestion}\nSelections: ${fullPath.join(' > ')}\nApply this plan in the org chart.`
        : suggestion;

    if (shouldEscalateToExecution) {
      logTiming('chip.click.escalate-budget', {
        timingId,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      await sendMessageInternal(composedPrompt, suggestion);
      return;
    }

    const findPrefetchedBranch = () =>
      suggestionTreeRef.current.get(suggestionPathKey(nextPath));
    const waitForPrefetchedBranch = async (
      pendingPrefetch: Promise<void>,
      maxWaitMs: number,
    ) => {
      const pollStepMs = 80;
      const deadline = performance.now() + maxWaitMs;
      while (performance.now() < deadline) {
        const branch = findPrefetchedBranch();
        if (branch && branch.options.length > 0) return branch;
        await Promise.race([
          pendingPrefetch,
          new Promise((resolve) => window.setTimeout(resolve, pollStepMs)),
        ]);
      }
      return findPrefetchedBranch();
    };
    let prefetched = findPrefetchedBranch();
    let pendingPrefetch: Promise<void> | undefined;

    if (!prefetched) {
      const currentQuestion = currentSuggestionQuestionRef.current;
      const currentOptions = currentSuggestionOptionsRef.current;
      if (currentQuestion && currentOptions.length >= 2) {
        const pendingKey = suggestionRequestKey(
          currentQuestion,
          currentOptions,
          currentPath,
        );
        pendingPrefetch = suggestionPrefetchInFlightRef.current.get(pendingKey);
        if (pendingPrefetch) {
          prefetched = await waitForPrefetchedBranch(
            pendingPrefetch,
            getSuggestionPrefetchJoinWaitMs(suggestionLatencyEwmaRef.current),
          );
        }
      }
    }

    if (prefetched && prefetched.options.length > 0) {
      const prefetchedOptions = sanitizeSuggestionList(prefetched.options);
      if (prefetchedOptions.length === 0) {
        await sendMessageInternal(composedPrompt, suggestion);
        return;
      }
      const userMessage: ChatMessage = {
        id: generateId('msg'),
        role: 'user',
        parts: [{ type: 'text', text: suggestion }],
        createdAt: Date.now(),
      };
      const assistantMessage: ChatMessage = {
        id: generateId('msg'),
        role: 'assistant',
        parts: [{ type: 'text', text: prefetched.question }],
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setDynamicSuggestions(prefetchedOptions);
      currentSuggestionQuestionRef.current = prefetched.question;
      currentSuggestionOptionsRef.current = prefetchedOptions;
      suggestionPathRef.current = nextPath;
      const seedMessages = [...messagesRef.current, userMessage, assistantMessage];
      void prefetchSuggestionTree(
        prefetched.question,
        prefetchedOptions,
        seedMessages,
        nextPath,
      );
      logTiming('chip.click.prefetch-hit', {
        timingId,
        elapsedMs: Math.round(performance.now() - startedAt),
        optionCount: prefetchedOptions.length,
      });
      return;
    }

    // On-demand fast-path: fetch next options for current chip set before escalating to full model turn
    const currentQuestion = currentSuggestionQuestionRef.current;
    const currentOptions = currentSuggestionOptionsRef.current;
    if (currentQuestion && currentOptions.length >= 2) {
      const onDemand = await fetchSuggestionChildren(
        currentQuestion,
        currentOptions,
        currentPath,
        messagesRef.current
          .slice(-8)
          .map((m) => m.parts.find((p) => p.type === 'text'))
          .filter((p): p is { type: 'text'; text: string } => p?.type === 'text')
          .map((p) => p.text),
        getAdaptiveChildrenTimeoutMs(),
      );
      const branch = onDemand?.branches?.find(
        (b) => normalizeSuggestionToken(b.option) === normalizeSuggestionToken(suggestion),
      );
      const branchOptions = sanitizeSuggestionList(branch?.options ?? []);
      if (branch && branchOptions.length > 0) {
        const userMessage: ChatMessage = {
          id: generateId('msg'),
          role: 'user',
          parts: [{ type: 'text', text: suggestion }],
          createdAt: Date.now(),
        };
        const assistantMessage: ChatMessage = {
          id: generateId('msg'),
          role: 'assistant',
          parts: [{ type: 'text', text: branch.question }],
          createdAt: Date.now(),
        };
        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setDynamicSuggestions(branchOptions);
        currentSuggestionQuestionRef.current = branch.question;
        currentSuggestionOptionsRef.current = branchOptions;
        suggestionPathRef.current = nextPath;
        const seedMessages = [...messagesRef.current, userMessage, assistantMessage];
        void prefetchSuggestionTree(
          branch.question,
          branchOptions,
          seedMessages,
          nextPath,
        );
        logTiming('chip.click.ondemand-hit', {
          timingId,
          elapsedMs: Math.round(performance.now() - startedAt),
          optionCount: branchOptions.length,
        });
        return;
      }

      // If on-demand timed out and prefetch is still in flight, give stream a short grace window
      // before escalating to a full model turn.
      if (pendingPrefetch) {
        const latePrefetch = await waitForPrefetchedBranch(pendingPrefetch, 1200);
        const lateOptions = sanitizeSuggestionList(latePrefetch?.options ?? []);
        if (latePrefetch && lateOptions.length > 0) {
          const userMessage: ChatMessage = {
            id: generateId('msg'),
            role: 'user',
            parts: [{ type: 'text', text: suggestion }],
            createdAt: Date.now(),
          };
          const assistantMessage: ChatMessage = {
            id: generateId('msg'),
            role: 'assistant',
            parts: [{ type: 'text', text: latePrefetch.question }],
            createdAt: Date.now(),
          };
          setMessages((prev) => [...prev, userMessage, assistantMessage]);
          setDynamicSuggestions(lateOptions);
          currentSuggestionQuestionRef.current = latePrefetch.question;
          currentSuggestionOptionsRef.current = lateOptions;
          suggestionPathRef.current = nextPath;
          const seedMessages = [...messagesRef.current, userMessage, assistantMessage];
          void prefetchSuggestionTree(
            latePrefetch.question,
            lateOptions,
            seedMessages,
            nextPath,
          );
          logTiming('chip.click.prefetch-late-hit', {
            timingId,
            elapsedMs: Math.round(performance.now() - startedAt),
            optionCount: lateOptions.length,
          });
          return;
        }
      }
    }

    logTiming('chip.click.escalate-fallback', {
      timingId,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
    await sendMessageInternal(composedPrompt, suggestion);
  }, [
    sendMessageInternal,
    fetchSuggestionChildren,
    getAdaptiveChildrenTimeoutMs,
    prefetchSuggestionTree,
    logTiming,
  ]);

  const dismissPendingForm = useCallback(() => {
    setPendingFormTurnId(null);
  }, []);

  const applyLensRecommendations = useCallback(async (recommendations: AgentLensRecommendation[]) => {
    const context = {
      diagramData: getCurrentDiagramData(),
      structuralDiagramData,
      diagramType,
      selectedNodeIds,
      canWrite,
      commandManager,
      stores,
    };
    const calls: AgentToolCall[] = recommendations.map((r) => ({ name: r.toolName, params: r.params }));
    const results = await registry.executeToolCalls(calls, context);
    const applied = results.filter((r) => r.result.success).length;
    const failedResults = results.filter((r) => !r.result.success);
    // If any focus change was applied, request viewport fit so the canvas pans to it
    const hasFocusChange = results.some((r) => r.call.name === 'focus_view' && r.result.success);
    if (hasFocusChange) {
      stores.orgchartView?.requestViewportFit?.();
    }
    const failureSummary = failedResults.length > 0
      ? ` (${failedResults.map((r) => r.result.summary).join('; ')})`
      : '';
    const skippedPart = failedResults.length > 0
      ? t('assistant.lensApplySkippedSuffix', {
          count: failedResults.length,
          defaultValue: ` (${failedResults.length} skipped)`,
        })
      : '';
    const summaryText = applied > 0
      ? t(
          applied === 1
            ? 'assistant.lensApplySummary_one'
            : 'assistant.lensApplySummary_other',
          {
            count: applied,
            skipped: skippedPart,
            defaultValue:
              applied === 1
                ? `Applied 1 view change.${failedResults.length > 0 ? ` (${failedResults.length} skipped)` : ''}`
                : `Applied ${applied} view changes.${failedResults.length > 0 ? ` (${failedResults.length} skipped)` : ''}`,
          },
        )
      : `${t('assistant.lensApplyNone', { defaultValue: 'No changes applied.' })}${failureSummary}`;
    const summaryMessage: ChatMessage = {
      id: generateId('msg'),
      role: 'assistant',
      parts: [{ type: 'text', text: summaryText }],
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, summaryMessage]);
    setPendingLensTurnId(null);

    // Continue the conversation: silently re-trigger the LLM so it proceeds with analysis
    if (applied > 0) {
      const appliedSummaries = results
        .filter((r) => r.result.success)
        .map((r) => r.result.summary)
        .join('; ');
      await sendMessageInternal(
        `[view_applied] The requested view changes have been applied (${appliedSummaries}). Please proceed — propose how to approach the goal and offer concrete next steps or choices.`,
        undefined,
        true, // silent: don't show a user bubble
      );
    }
  }, [registry, getCurrentDiagramData, structuralDiagramData, diagramType, selectedNodeIds, canWrite, commandManager, stores, sendMessageInternal, t]);

  const executeFormAutoTool = useCallback(async (
    autoExecute: AgentFormAutoExecute,
    formValues: Record<string, string>,
  ) => {
    const context = {
      diagramData: getCurrentDiagramData(),
      structuralDiagramData,
      diagramType,
      selectedNodeIds,
      canWrite,
      commandManager,
      stores,
    };

    // Merge fixed params with user's form values (auto-convert numbers)
    const params: Record<string, unknown> = { ...autoExecute.fixedParams };
    for (const [key, value] of Object.entries(formValues)) {
      if (!value.trim()) continue;
      const num = Number(value);
      params[key] = !isNaN(num) && value.trim() !== '' ? num : value;
    }

    const results = await registry.executeToolCalls([{ name: autoExecute.tool, params }], context);
    const result = results[0];

    const parts: ChatMessagePart[] = [];
    if (result.result.preview) {
      parts.push({ type: 'preview', toolName: autoExecute.tool, result: result.result });
    }
    if (result.result.formRequest) {
      parts.push({ type: 'form-input', toolName: autoExecute.tool, result: result.result });
    }
    if (result.result.success) {
      parts.push({ type: 'text', text: result.result.summary });
    } else {
      parts.push({ type: 'text', text: result.result.summary });
    }
    parts.push({ type: 'tool-result', toolName: autoExecute.tool, params, result: result.result });

    const assistantMessage: ChatMessage = {
      id: generateId('msg'),
      role: 'assistant',
      parts,
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    if (result.result.preview) {
      setPendingPreviewTurnId(assistantMessage.id);
    }
    if (result.result.formRequest) {
      setPendingFormTurnId(assistantMessage.id);
    }

    // On failure: fall back to LLM with error context
    if (!result.result.success) {
      await sendMessageInternal(
        `[auto_tool_failed] ${autoExecute.tool} failed: ${result.result.summary}. Form values: ${JSON.stringify(formValues)}.`,
        undefined,
        true,
      );
    }
  }, [registry, getCurrentDiagramData, structuralDiagramData, diagramType, selectedNodeIds, canWrite, commandManager, stores, sendMessageInternal]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setPendingPreviewTurnId(null);
    setPendingFormTurnId(null);
    setPendingLensTurnId(null);
    setDynamicSuggestions(null);
    directPreviewResultsRef.current.clear();
    resolvedPreviewMessageIdsRef.current.clear();
    resetSuggestionTree();
  }, [resetSuggestionTree]);

  return {
    messages,
    isLoading,
    loadingTrace,
    liveThinkingText,
    sendMessage,
    selectSuggestion,
    cancel,
    clearMessages,
    actionHistory,
    pendingPreviewTurnId,
    confirmPendingPreview,
    rejectPendingPreview,
    pendingFormTurnId,
    dismissPendingForm,
    pendingLensTurnId,
    applyLensRecommendations,
    executeFormAutoTool,
    dynamicSuggestions,
    invokeTool,
    invokeToolSilently,
  };
}
